import { useEffect, useRef, useState } from 'react';
import { api, errorMessage } from '../lib/api';
import { env } from '../lib/env';

/** En el APK: selector de cuentas nativo de Android (Credential Manager). Devuelve el ID token o null si se canceló. */
let nativeInit: Promise<void> | null = null;
async function nativeGoogleLogin(webClientId: string): Promise<string | null> {
  const { SocialLogin } = await import('@capgo/capacitor-social-login');
  nativeInit ??= SocialLogin.initialize({ google: { webClientId } }).catch((e) => {
    nativeInit = null;
    throw e;
  });
  await nativeInit;
  try {
    const r = await SocialLogin.login({ provider: 'google', options: {} });
    return r.result.responseType === 'online' ? r.result.idToken : null;
  } catch (e) {
    if (/cancel/i.test(String((e as Error)?.message ?? e))) return null;
    throw e;
  }
}

type TokenResponse = { access_token?: string; error?: string; error_description?: string };
type TokenClient = { requestAccessToken(o?: { prompt?: string }): void };
type Gis = {
  accounts: {
    oauth2: {
      initTokenClient(o: { client_id: string; scope: string; callback: (r: TokenResponse) => void; error_callback?: (e: { type: string }) => void }): TokenClient;
    };
  };
};
declare global {
  interface Window {
    google?: Gis;
  }
}

let script: Promise<void> | null = null;
function loadGis(): Promise<void> {
  if (window.google?.accounts?.oauth2) return Promise.resolve();
  script ??= new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://accounts.google.com/gsi/client';
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => {
      script = null;
      reject(new Error('No se pudo cargar Google. Revisa tu conexión.'));
    };
    document.head.appendChild(s);
  });
  return script;
}

/** "G" oficial de Google (4 colores). */
function GoogleG() {
  return (
    <svg viewBox="0 0 48 48" className="size-5" aria-hidden>
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
    </svg>
  );
}

/**
 * Botón "Continúa con Google" con el estilo de la app (gris, a todo el ancho, como Uber).
 * Google abre su ventana de cuentas y entrega un token que el servidor verifica (debe ser de nuestra app).
 * En el APK se usa el selector de cuentas nativo de Android (Google no permite su página dentro de la vista web).
 * No se muestra si el servidor no tiene GOOGLE_CLIENT_ID.
 */
export function GoogleButton({ onCredential, onError }: { onCredential: (credential: string) => Promise<void>; onError: (msg: string) => void }) {
  const [clientId, setClientId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);
  const client = useRef<TokenClient | null>(null);
  const handler = useRef(onCredential);
  handler.current = onCredential;
  const errorRef = useRef(onError);
  errorRef.current = onError;

  useEffect(() => {
    void api<{ clientId: string | null }>('/auth/google/config')
      .then((c) => setClientId(c.clientId))
      .catch(() => setClientId(null));
  }, []);

  // Se precarga para que la ventana de Google se abra directamente al tocar (los navegadores bloquean ventanas tardías).
  useEffect(() => {
    if (!clientId) return;
    if (env.isNative) {
      setReady(true);
      return;
    }
    void loadGis()
      .then(() => {
        client.current = window.google!.accounts.oauth2.initTokenClient({
          client_id: clientId,
          scope: 'openid email profile',
          callback: (r) => {
            if (!r.access_token) {
              setBusy(false);
              if (r.error && r.error !== 'access_denied') errorRef.current('No se pudo iniciar sesión con Google');
              return;
            }
            handler.current(r.access_token)
              .catch((e) => errorRef.current(errorMessage(e)))
              .finally(() => setBusy(false));
          },
          error_callback: () => setBusy(false), // ventana cerrada por el usuario
        });
        setReady(true);
      })
      .catch((e: Error) => errorRef.current(e.message));
  }, [clientId]);

  if (!clientId) return null;
  return (
    <>
      <div className="flex items-center gap-3 text-sm text-[var(--text-muted)]" aria-hidden>
        <span className="h-px flex-1 bg-[var(--text-muted)]/40" /> o <span className="h-px flex-1 bg-[var(--text-muted)]/40" />
      </div>
      <button
        type="button"
        className="tap w-full h-14 rounded-xl bg-[var(--surface-2)] hover:brightness-95 flex items-center justify-center gap-3 font-medium disabled:opacity-60"
        disabled={busy || !ready}
        onClick={() => {
          setBusy(true);
          if (!env.isNative) return client.current?.requestAccessToken({ prompt: 'select_account' });
          void nativeGoogleLogin(clientId)
            .then((token) => (token ? handler.current(token) : undefined))
            .catch((e) => errorRef.current(errorMessage(e) || 'No se pudo iniciar sesión con Google'))
            .finally(() => setBusy(false));
        }}
      >
        <GoogleG /> {busy ? 'Conectando con Google…' : 'Continúa con Google'}
      </button>
    </>
  );
}
